import React, { useState, ChangeEvent, FormEvent } from "react";
import styled from "styled-components";
import {
  signInWithEmailPassword,
  signUpWithEmailPassword,
} from "../../auth/services/authService";

type FormMode = "sign-in" | "sign-up";

const FormCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 40px 36px;
  width: 100%;
  max-width: 420px;
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 2px solid #e8eaed;
  margin-bottom: 28px;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  background: none;
  border: none;
  padding: 10px 0;
  font-size: 15px;
  font-weight: ${(props) => (props.$isActive ? "600" : "400")};
  color: ${(props) => (props.$isActive ? "#1a1a2e" : "#888")};
  border-bottom: ${(props) => (props.$isActive ? "2px solid #1976d2" : "none")};
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #1976d2;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #444;
  margin-bottom: 6px;
`;

const Input = styled.input`
  border: 1.5px solid #d0d5dd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #1a1a2e;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: #1976d2;
  }

  &::placeholder {
    color: #b0b0b0;
  }
`;

const NameRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: #1976d2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #1558a8;
  }

  &:disabled {
    background: #90b8e8;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  font-size: 13px;
  color: #c62828;
  background: #ffebee;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0 0 12px;
`;

const SuccessMessage = styled.p`
  font-size: 13px;
  color: #2e7d32;
  background: #e8f5e9;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0 0 12px;
`;

const AuthForm: React.FC = () => {
  const [mode, setMode] = useState<FormMode>("sign-in");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  function handleModeChange(newMode: FormMode): void {
    setMode(newMode);
    setFormError(null);
    setFormSuccess(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
  }

  async function handleSignIn(): Promise<void> {
    const result = await signInWithEmailPassword(email, password);
    if (result.error) {
      setFormError(result.error);
    }
  }

  async function handleSignUp(): Promise<void> {
    const result = await signUpWithEmailPassword(
      firstName,
      lastName,
      email,
      password
    );
    if (result.error) {
      setFormError(result.error);
    } else {
      setFormSuccess(
        "Account created! Please check your email to confirm your address before signing in."
      );
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    setIsSubmitting(true);

    if (mode === "sign-in") {
      await handleSignIn();
    } else {
      await handleSignUp();
    }

    setIsSubmitting(false);
  }

  return (
    <FormCard>
      <TabRow>
        <TabButton
          type="button"
          $isActive={mode === "sign-in"}
          onClick={() => handleModeChange("sign-in")}
        >
          Sign In
        </TabButton>
        <TabButton
          type="button"
          $isActive={mode === "sign-up"}
          onClick={() => handleModeChange("sign-up")}
        >
          Create Account
        </TabButton>
      </TabRow>

      <form onSubmit={handleSubmit}>
        {mode === "sign-up" && (
          <NameRow>
            <FieldGroup style={{ flex: 1 }}>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFirstName(event.target.value)
                }
                required
                placeholder="Jane"
                autoComplete="given-name"
              />
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setLastName(event.target.value)
                }
                required
                placeholder="Smith"
                autoComplete="family-name"
              />
            </FieldGroup>
          </NameRow>
        )}

        <FieldGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
            required
            placeholder="you@cifsolutions.com"
            autoComplete="email"
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setPassword(event.target.value)
            }
            required
            placeholder="••••••••"
            autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
          />
        </FieldGroup>

        {formError !== null && <ErrorMessage>{formError}</ErrorMessage>}
        {formSuccess !== null && <SuccessMessage>{formSuccess}</SuccessMessage>}

        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? "Please wait..."
            : mode === "sign-in"
            ? "Sign In"
            : "Create Account"}
        </SubmitButton>
      </form>
    </FormCard>
  );
};

export default AuthForm;
